import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X, Mail, Copy, Download, Sparkles } from "lucide-react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const KINDS = [
  { value: "cover_letter", label: "Cover letter" },
  { value: "resignation", label: "Resignation letter" },
  { value: "complaint", label: "Complaint letter" },
  { value: "recommendation", label: "Recommendation letter" },
  { value: "thank_you", label: "Thank-you letter" },
  { value: "custom", label: "Custom letter" },
];

export default function LetterBuilderPage() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    kind: "cover_letter",
    sender_name: "",
    sender_email: "",
    recipient_name: "",
    recipient_company: "",
    target_role: "",
    tone: "professional",
    extra_context: "",
  });
  const [points, setPoints] = useState([""]);
  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.sender_name) return toast.error("Please enter your name");
    setLoading(true);
    setOutput(null);
    try {
      const payload = {
        ...form,
        key_points: points.filter(Boolean),
      };
      const { data } = await api.post("/documents/letter", payload);
      setOutput(data);
      toast.success("Letter generated! +2 points · +£0.05 cashback");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate letter");
    } finally { setLoading(false); }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(output.content);
    toast.success("Letter copied to clipboard");
  };
  const download = () => {
    if (!output?.doc_id) return;
    window.open(`${API}/documents/${output.doc_id}/pdf`, "_blank");
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Document Creator · Letter</p>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">Letter writer</h1>
      <p className="mt-2 text-muted-foreground">Pick a letter type, fill in a few details, and get a polished letter you can send.</p>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <form onSubmit={submit} className="space-y-5 rounded-2xl border border-border bg-card p-6 md:p-8" data-testid="letter-form">
          <div className="space-y-1.5">
            <Label>Letter type</Label>
            <Select value={form.kind} onValueChange={(v) => update("kind", v)}>
              <SelectTrigger className="h-11" data-testid="letter-kind-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                {KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Your name *" value={form.sender_name} onChange={(v) => update("sender_name", v)} testid="letter-sender-name" />
            <Field label="Your email" value={form.sender_email} onChange={(v) => update("sender_email", v)} testid="letter-sender-email" type="email" />
            <Field label="Recipient name" value={form.recipient_name} onChange={(v) => update("recipient_name", v)} testid="letter-recipient-name" />
            <Field label="Recipient company" value={form.recipient_company} onChange={(v) => update("recipient_company", v)} testid="letter-recipient-company" />
          </div>
          <Field label="Subject / role (if relevant)" value={form.target_role} onChange={(v) => update("target_role", v)} testid="letter-subject" />

          <div className="space-y-1.5">
            <Label>Tone</Label>
            <Select value={form.tone} onValueChange={(v) => update("tone", v)}>
              <SelectTrigger className="h-11" data-testid="letter-tone-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="warm">Warm</SelectItem>
                <SelectItem value="assertive">Assertive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Key points to include</Label>
              <Button type="button" size="sm" variant="ghost" onClick={() => setPoints((arr) => [...arr, ""])} data-testid="letter-add-point">
                <Plus className="mr-1 h-3.5 w-3.5" /> Add
              </Button>
            </div>
            {points.map((p, i) => (
              <div key={i} className="flex gap-2">
                <Input value={p} onChange={(e) => setPoints((arr) => arr.map((x, idx) => idx === i ? e.target.value : x))} placeholder={`Point ${i + 1}`} className="h-10" data-testid={`letter-point-${i}`} />
                {points.length > 1 && (
                  <Button type="button" variant="ghost" size="icon" onClick={() => setPoints((arr) => arr.filter((_, idx) => idx !== i))}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          <FieldTextarea label="Additional context" value={form.extra_context} onChange={(v) => update("extra_context", v)} testid="letter-extra-context" />

          <Button type="submit" disabled={loading} className="h-11 w-full" data-testid="letter-submit">
            <Sparkles className="mr-2 h-4 w-4" /> {loading ? "Generating…" : "Generate letter"}
          </Button>
        </form>

        <div className="space-y-3" data-testid="letter-output-panel">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl"><Mail className="mr-2 inline h-4 w-4" /> Output</h2>
            {output && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={copy} data-testid="letter-copy"><Copy className="mr-1 h-3.5 w-3.5" /> Copy</Button>
                <Button size="sm" onClick={download} data-testid="letter-download"><Download className="mr-1 h-3.5 w-3.5" /> Download</Button>
              </div>
            )}
          </div>
          <div className="min-h-[400px] rounded-2xl border border-border bg-card p-6 md:p-8">
            {output ? (
              <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed">{output.content}</pre>
            ) : (
              <div className="flex h-full min-h-[400px] flex-col items-center justify-center text-center text-muted-foreground">
                <Mail className="h-8 w-8 opacity-40" />
                <p className="mt-3 text-sm">Your letter will appear here.</p>
              </div>
            )}
          </div>
          {output && (
            <Button variant="ghost" onClick={() => nav("/documents")} className="w-full text-sm">View all saved documents →</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="h-11" data-testid={testid} />
    </div>
  );
}
function FieldTextarea({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testid} />
    </div>
  );
}
